export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

export const toggleSort = <K extends string>(
  current: SortState<K>,
  nextKey: K,
  defaultDirection: SortDirection = 'asc',
): SortState<K> => {
  if (current.key !== nextKey) {
    return { key: nextKey, direction: defaultDirection };
  }

  return {
    key: nextKey,
    direction: current.direction === 'asc' ? 'desc' : 'asc',
  };
};

export const compareValues = (
  left: string | number | undefined,
  right: string | number | undefined,
  direction: SortDirection,
): number => {
  const normalize = (value: string | number | undefined): string | number => {
    if (value === undefined || value === null) {
      return '';
    }
    return typeof value === 'string' ? value.toLowerCase() : value;
  };

  const a = normalize(left);
  const b = normalize(right);

  let result = 0;
  if (typeof a === 'number' && typeof b === 'number') {
    result = a - b;
  } else {
    result = String(a).localeCompare(String(b), 'ru');
  }

  return direction === 'asc' ? result : -result;
};

export const formatCurrency = (value: number): string => `RUB ${Number(value || 0).toLocaleString('ru-RU')}`;

/** Бизнес-ID договора в таблицах и операциях PlanFact (поле «Сделка»). */
export const formatContractRecordId = (id: number): string => `CTR${id}`;

/** Сумма договора в таблицах: целое с разрядностью по-русски, без копеек и без символа валюты. */
export const formatContractSumDisplay = (value: number | string | undefined | null): string => {
  if (value == null || value === '') {
    return '—';
  }
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) {
    return '—';
  }
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n));
};

/** Разбирает ввод суммы с пробелами-разделителями (например «1 000 000»). */
export const parseContractSumInput = (value: string): number | undefined => {
  const trimmed = value.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

/** Оставляет в черновике только цифры. */
export const normalizeContractSumDraft = (value: string): string => value.replace(/\D/g, '');

/** Форматирует черновик суммы для поля ввода. */
export const displayContractSumDraft = (stored: string): string => {
  if (!stored.trim()) return '';
  const n = Number(stored);
  if (!Number.isFinite(n)) return stored;
  return formatContractSumDisplay(n);
};

export const formatDate = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

/** DD.MM.YY — для компактных таблиц (Задачи, Договоры). */
export const formatDateShort = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}.${month}.${year}`;
};

/** Дата и время в MSK (DD.MM.YY HH:MM) — для synced_at PlanFact. */
export const formatDateTimeMsk = (value?: string): string => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const parts = new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${pick('day')}.${pick('month')}.${pick('year')} ${pick('hour')}:${pick('minute')}`;
};
